<script setup>
import { computed, ref, watch, onMounted, onUpdated } from "vue";
import { useStore } from "vuex";
import initDesignUploadModal from "components/jiffy/design-upload-cta/design-upload-cta";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import Spinner from "components/jiffy/vue-cart/Spinner.vue";
import { useDtfStore } from "components/jiffy/vue-product-details-dtf/store";
import { getQueryParams, removeQueryParam } from "common/utils/browser";
import { getFileFromUrl } from "common/utils/file";
import { parseArtworkUrls } from "common/utils/json";
import { trackRudderstackImagePlacementSelection } from "common/utils/rudderstack/tracker";
import { getImageInfo } from "../utils/preload-image";
import {
  trackUploadSuccess,
  trackRemoveImage,
  trackCustomPlacementInteraction
} from "../utils/tracker";
import { NECK_LINE_MODES, SURFACES, MODES } from "../misc/constants";
import ArtworkCanvas from "./ArtworkCanvas.vue";
import ArtworkDesign from "./ArtworkDesign.vue";
import ArtworkMeasurement from "./ArtworkMeasurement.vue";
import ArtworkError from "./ArtworkError.vue";
import ArtworkLegend from "./ArtworkLegend.vue";
import { getDisplayedSize, getSizeWithSteps } from "../utils/sizes";

const props = defineProps({
  sideKey: {
    type: String,
    required: true
  },
  titlePrefix: {
    type: String,
    required: true
  },
  availablePlacements: {
    type: Array,
    required: true
  },
  isPreview: {
    type: Boolean,
    default: false
  }
});

const store = useStore();
const dtfStore = useDtfStore();
const dtfChannelConnected = computed(() => dtfStore.actionCableConnected);
const legendOnceShown = ref(true);
const legendShown = ref(false);
const artworkReference = ref(null);
const isInputTouched = ref(false);
const isRestoring = computed(() => store.getters["app/isRestoring"]);
const supportsMade = computed(() => store.getters["app/supportsMade"]);
const neckLineMode = computed(() => store.getters[`surface_${props.sideKey}/neckLineMode`]);
const neckLineLabel = computed(() => store.getters[`surface_${props.sideKey}/neckLineLabel`]);
const surfaceLabel = computed(() => store.getters[`surface_${props.sideKey}/surfaceLabel`]);
let previousSurfaceLabel = surfaceLabel.value;
const neckLines = computed(() => store.getters["product/neckLines"][surfaceLabel.value]);
const getNeckLine = label => store.getters["product/getNeckLine"](surfaceLabel.value, label);
const filteredNeckLines = computed(() => {
  const filledSizes = store.getters["app/filledSizes"];
  const lines = neckLines.value || {};
  return Object.keys(lines).reduce((acc, key) => {
    if (filledSizes.includes(key)) {
      acc[key] = lines[key];
    }
    return acc;
  }, {});
});
const disabledCustomization = computed(() => !Object.keys(filteredNeckLines.value).length);
const shownCustomNeckLines = computed(
  () => neckLines.value && neckLineMode.value === NECK_LINE_MODES.CUSTOM
);
const uploadErrorText = computed(() => store.getters[`artwork_${props.sideKey}/uploadErrorText`]);
const artworkExists = computed(() => store.getters[`artwork_${props.sideKey}/artworkExists`]);
const artworkSize = computed(() => store.getters[`artwork_${props.sideKey}/artworkSize`]);
const currentArtwork = computed(() => store.getters[`artwork_${props.sideKey}/artwork`]);
let cachedSize = store.getters[`artwork_${props.sideKey}/artworkSize`];
/* The `nonZeroArtworkSize` computed property is used to prevent showing the artwork size is 0x0 in inputs. */
const nonZeroArtworkSize = computed(() => {
  const currentSize = store.getters[`artwork_${props.sideKey}/artworkSize`];
  if (!currentSize.width && !currentSize.height) return cachedSize;
  cachedSize = currentSize;
  return currentSize;
});
const artworkUrl = computed(() => store.getters[`artwork_${props.sideKey}/artworkUrl`]);
const surface = computed(() => store.getters[`surface_${props.sideKey}/surface`]);
const downscaleFactor = computed(() => store.getters[`surface_${props.sideKey}/downscaleFactor`]);
const setDownscaleFactor = value =>
  store.commit(`surface_${props.sideKey}/setDownscaleFactor`, value);
const oneInch = computed(() => store.getters[`surface_${props.sideKey}/oneInch`]);
const pxToInch = size => store.getters[`surface_${props.sideKey}/convertPxToInches`](size);
const inchToPx = size => store.getters[`surface_${props.sideKey}/convertInchesToPx`](size);
const getSurface = label => store.getters[`surface_${props.sideKey}/getSurface`](label);
const title = computed(() => `${props.titlePrefix} Image Placement`);
const customNeckLines = computed(() => store.getters[`surface_${props.sideKey}/customNeckLines`]);
const maxLineHeight = max => getSizeWithSteps(max - pxToInch(artworkSize.value.height));
const currentNeckLineIn = computed(() => {
  const label = neckLineLabel.value;
  const { default: defaultLineValue, max_height: max } = getNeckLine(label);
  const customLineValue = customNeckLines.value[label] ?? defaultLineValue;
  const minValue = shownCustomNeckLines.value
    ? Math.min(customLineValue, maxLineHeight(max))
    : defaultLineValue;
  return getDisplayedSize(minValue);
});
const currentNeckLinePx = computed(() => inchToPx(currentNeckLineIn.value));
const maxCanvasHeight = computed(() => inchToPx(getNeckLine(neckLineLabel.value).max_height));
const rudderstackTrackProperties = computed(() => ({
  placement: surfaceLabel.value,
  artwork_id: currentArtwork.value.id,
  design_dimensions: [
    {
      width: pxToInch(artworkSize.value.width),
      height: pxToInch(artworkSize.value.height)
    }
  ]
}));

const hideLegend = () => {
  if (artworkExists.value && legendShown.value) {
    legendShown.value = false;
    legendOnceShown.value = false;
  }
};

const toggleLegend = () => {
  if (
    neckLineMode.value === NECK_LINE_MODES.CUSTOM &&
    legendOnceShown.value &&
    surfaceLabel.value !== SURFACES.FRONT_LEFT_CHEST
  ) {
    legendShown.value = true;
  } else {
    hideLegend();
  }
};

const updateSurfaceLabel = label => {
  previousSurfaceLabel = surfaceLabel.value;
  clearArtworkSize();
  store.commit(`surface_${props.sideKey}/setSurfaceLabel`, label);
  toggleLegend();
  trackRudderstackImagePlacementSelection("Selected", rudderstackTrackProperties.value);
};

const updateNeckLineLabel = label => {
  store.commit(`surface_${props.sideKey}/setNeckLineLabel`, label);
};

const updateNeckLineHeight = ({ label, height }) => {
  const { max_height: max } = getNeckLine(label);
  store.commit(`surface_${props.sideKey}/setNeckLineHeight`, {
    neckLineLabel: label,
    height: Math.max(Math.min(height, maxLineHeight(max)), 0)
  });
};

const updateNeckLineHeightFromCanvas = height => {
  updateNeckLineHeight({ label: neckLineLabel.value, height: pxToInch(height) });
};

const endNeckLineHeightFromCanvas = height => {
  const roundedHeight = getSizeWithSteps(pxToInch(height));
  updateNeckLineHeight({ label: neckLineLabel.value, height: roundedHeight });
};

const updateNeckLineCustomization = mode => {
  store.commit(`surface_${props.sideKey}/setNeckLineMode`, mode);
  toggleLegend();
};

const clearCustomNeckLines = () => {
  store.dispatch(`surface_${props.sideKey}/clearNeckLines`);
};

const removeArtwork = () => {
  const { filetype, filesize, filename } = currentArtwork.value;
  const storedRudderstackTrackProperties = rudderstackTrackProperties.value;
  isInputTouched.value = false;

  // eslint-disable-next-line no-alert
  if (window.confirm("Do you really want to delete the artwork?")) {
    hideLegend();
    store.dispatch(`artwork_${props.sideKey}/removeArtwork`);
    clearCustomNeckLines();
    if (surfaceLabel.value === SURFACES.FRONT_LEFT_CHEST) {
      updateSurfaceLabel(SURFACES.FRONT);
    }
    trackRemoveImage({ location: props.sideKey, filetype, filesize, filename });
    trackRudderstackImagePlacementSelection("Removed", storedRudderstackTrackProperties);
  }
};

const endResize = size => {
  const isIncreasedHeight = size.height > artworkSize.value.height;
  store.commit(`artwork_${props.sideKey}/setArtworkSize`, {
    ...size,
    widthInch: pxToInch(size.width),
    heightInch: pxToInch(size.height)
  });
  if (isIncreasedHeight) {
    updateNeckLineHeight({ label: neckLineLabel.value, height: currentNeckLineIn.value });
  }

  trackRudderstackImagePlacementSelection("Size Edited", rudderstackTrackProperties.value);
};

let previousNeckLineLabel = neckLineLabel.value;
let previousNeckLineHeight = currentNeckLineIn.value;
let previousArtworkWidth = pxToInch(artworkSize.value.width);
let previousArtworkHeight = pxToInch(artworkSize.value.height);

const initPreviousValues = () => {
  previousNeckLineLabel = neckLineLabel.value;
  previousNeckLineHeight = currentNeckLineIn.value;
  previousArtworkWidth = pxToInch(artworkSize.value.width);
  previousArtworkHeight = pxToInch(artworkSize.value.height);
};

const initArtworkSize = size => {
  // Detect if we're changing to front-left-chest placement
  const isChangingToFrontLeftChest =
    surfaceLabel.value === SURFACES.FRONT_LEFT_CHEST &&
    previousSurfaceLabel !== SURFACES.FRONT_LEFT_CHEST;

  // Use the incoming size parameter directly when changing to front-left-chest
  // otherwise use the cached size if input is touched
  if (isInputTouched.value && !isChangingToFrontLeftChest) {
    size = nonZeroArtworkSize.value;
  }

  const { max_height: max, default: defaultNeckLine } = getNeckLine(neckLineLabel.value);
  const newHeight = Math.min(size.height, inchToPx(max - defaultNeckLine));
  const newWidth = (size.width / size.height) * newHeight;
  const newSize = {
    width: newWidth,
    height: newHeight,
    widthInch: pxToInch(newWidth),
    heightInch: pxToInch(newHeight)
  };
  store.commit(`artwork_${props.sideKey}/setArtworkSize`, newSize);

  initPreviousValues();
};

const resetNeckLineWithArtwork = ({ label, height }) => {
  const { max_height: max } = getNeckLine(label);
  const { width: aWidth, height: aHeight } = artworkSize.value;
  const newHeight = Math.min(inchToPx(max - height), aHeight);
  const newWidth = (aWidth / aHeight) * newHeight;
  endResize({ width: newWidth, height: newHeight });
  updateNeckLineHeight({ label, height });
};

const clearArtworkSize = () => {
  store.commit(`artwork_${props.sideKey}/clearArtworkSize`);
};

const getInteractionType = () => {
  if (
    neckLineLabel.value !== previousNeckLineLabel ||
    currentNeckLineIn.value !== previousNeckLineHeight
  ) {
    return "moved";
  }
  if (
    pxToInch(artworkSize.value.width) !== previousArtworkWidth ||
    pxToInch(artworkSize.value.height) !== previousArtworkHeight
  ) {
    return "resized";
  }
  return "no_change";
};

const trackCustomPlacement = (changeType = "drag") => {
  let interactionType = changeType;
  if (interactionType === "drag") {
    interactionType = getInteractionType();
  }

  if (
    currentArtwork.value === null ||
    neckLineLabel.value === "default" ||
    interactionType === "no_change"
  ) {
    return;
  }

  const payload = {
    interaction_type: interactionType,
    placement: surfaceLabel.value,
    artwork_id: currentArtwork.value.id,
    placement_details: {
      neck_line: {
        label: neckLineLabel.value,
        height: currentNeckLineIn.value
      },
      design_dimensions: {
        width: pxToInch(artworkSize.value.width),
        height: pxToInch(artworkSize.value.height)
      }
    }
  };

  trackCustomPlacementInteraction(payload);
  initPreviousValues();
};

watch(artworkExists, artwork => {
  artwork?.url && trackUploadSuccess(props.sideKey, artwork.file, artwork);
});

const autoUploadArtwork = async () => {
  const { artwork_urls: imageUrls, file_content_type: imageType, side } = getQueryParams();
  if (!imageUrls || !imageType) return;

  // Determine which side to attach artwork to
  // Only accept 'back', otherwise default to 'front'
  const targetSide = side === "back" ? "back" : "front";

  // Only process if this component matches the target side
  if (props.sideKey !== targetSide) return;

  removeQueryParam("artwork_urls");
  removeQueryParam("file_content_type");
  removeQueryParam("side");

  const parsedUrls = parseArtworkUrls(imageUrls);
  if (!parsedUrls) return;

  const { imageUrl, lastArtworkId: existingArtworkId } = parsedUrls;

  const [file] = await Promise.all([
    getFileFromUrl(imageUrl, `artwork_image-${Date.now()}`, imageType),
    store.dispatch(`artwork_${targetSide}/removeArtwork`)
  ]);

  store.commit("app/setMode", MODES.MADE);
  store.dispatch(`artwork_${targetSide}/upload`, { file, existingArtworkId });

  if (artworkReference.value) {
    artworkReference.value.scrollIntoView({ behavior: "smooth" });
  }
};

watch(dtfChannelConnected, isConnected => {
  if (isConnected && surface.value) {
    getImageInfo(surface.value.picture).finally(autoUploadArtwork);
  }
});

onMounted(() => {
  initPreviousValues();
});

onUpdated(() => {
  initDesignUploadModal();
});
</script>

<template>
  <div
    ref="artworkReference"
    class="artwork"
    :class="{ 'artwork--disabled': !supportsMade }"
    data-testid="artwork"
  >
    <div class="artwork__title-wrapper">
      <div class="artwork__title" data-testid="artwork-title">{{ title }}</div>
      <button
        v-if="artworkExists"
        class="artwork__remove-artwork"
        type="button"
        aria-label="Remove artwork"
        data-testid="remove-artwork"
        @click="removeArtwork"
      >
        <UseSvg icon="trash-print" />
        <span class="artwork__delete-text">Delete</span>
      </button>
    </div>

    <div v-if="uploadErrorText" class="artwork__error" data-testid="artwork-error">
      <ArtworkError :message="uploadErrorText" />
    </div>

    <div class="artwork__shadow-wrapper made-monetization-enabled">
      <div v-if="isRestoring" class="artwork__loader">
        <Spinner class="artwork__spinner" />
      </div>
      <div
        class="artwork__canvas"
        :class="{ 'artwork__canvas--preview': props.isPreview }"
        data-testid="artwork-canvas"
        @click="hideLegend"
      >
        <ArtworkCanvas
          :key="surface.picture"
          :artwork-url="artworkUrl"
          :surface="surface"
          :max-boundings-height="maxCanvasHeight"
          :surface-label="surfaceLabel"
          :get-surface="getSurface"
          :downscale-factor="downscaleFactor"
          :current-neck-line-px="currentNeckLinePx"
          :shown-canvas-border="artworkExists"
          :shown-custom-neck-lines="shownCustomNeckLines"
          @update-downscale-factor="setDownscaleFactor"
          @update-neck-line-height="updateNeckLineHeightFromCanvas"
          @end-neck-line-height="endNeckLineHeightFromCanvas"
          @track-custom-placement="trackCustomPlacement"
          @init-previous-values="initPreviousValues"
          @mouseup="trackCustomPlacement('drag')"
        >
          <template #measurement>
            <ArtworkMeasurement
              v-if="artworkExists"
              :get-surface="getSurface"
              :surface-label="surfaceLabel"
              :downscale-factor="downscaleFactor"
              :shown-custom-neck-lines="shownCustomNeckLines"
              :neck-line-label="neckLineLabel"
              :current-neck-line-in="currentNeckLineIn"
              :current-neck-line-px="currentNeckLinePx"
            />
          </template>

          <template #default>
            <template v-if="artworkExists">
              <ArtworkDesign
                :surface-label="surfaceLabel"
                :surface="surface"
                :one-inch="oneInch"
                :artwork-url="artworkUrl"
                :artwork-size="artworkSize"
                @resize="endResize"
                @init-size="initArtworkSize"
                @clear-artwork-size="clearArtworkSize"
              />
              <ArtworkLegend v-if="legendShown" :disabled="disabledCustomization" />
            </template>
          </template>

          <template #after-canvas-wrapper>
            <slot v-if="!artworkExists" name="upload"></slot>
          </template>
        </ArtworkCanvas>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/print-settings";
@import "frontend/init/jiffy/mixin";
@import "frontend/init/jiffy/button/mixin";

.artwork {
  font-family: $pod-font-family;

  &--disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  @include with-container(container, $pod-pdp-desktop-response) {
    min-height: 100%;
    grid-template-columns: 100%;
    display: grid;
    grid-template-rows: min-content 1fr;
  }

  &__title-wrapper {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    position: relative;
  }

  &__title-price {
    position: absolute;
    top: -10px;
    right: 0;
  }

  &__error {
    margin-bottom: 10px;
  }

  &__title {
    font-size: 16px;
    font-weight: 500;
    color: $black;
  }

  &__delete-text {
    font-size: 16px;
    font-weight: 500;
    color: $black;
    padding-right: 5px;
  }

  &__remove-artwork {
    @include clear-button;
    @include svg-wrapper;

    padding: 4px;
    width: 100px;
    height: 32px;
    border: 1px solid $pod-gray-modern-200;
    border-radius: 6px;
    background: $pod-danger-light;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__canvas {
    margin: 0 (-$mobile-spacing);

    @include with-tablet {
      margin: 0;
    }

    @include with-container(container, $pod-pdp-desktop-response) {
      border-radius: 4px;
      overflow: hidden;
    }

    &--preview {
      @include with-container(container, $pod-pdp-desktop-response) {
        max-height: 520px;
      }
    }
  }

  &__controls-bg-wrapper {
    margin: 14px 0 11px;

    &--bg {
      border-radius: 4px;
      background-color: $pod-gray-modern-100;
      padding: 12px;
    }
  }

  &__controls {
    &--disabled {
      pointer-events: none;
      opacity: 0.5;
    }

    & + & {
      margin-top: 14px;
    }
  }

  &__controls-title {
    margin-bottom: 4px;

    &--bold {
      font-weight: bold;
    }
  }

  &__shadow-wrapper {
    @include with-container(container, $pod-pdp-desktop-response) {
      padding: 8px;
      border-radius: 8px;
      border: 1px solid $pod-gray-modern-200;
      background-color: $white;
      box-shadow: $pod-card-shadow;
    }

    &.made-monetization-enabled {
      position: relative;
    }
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba($black, 0.6);
  }

  &__spinner {
    width: 30px;
  }

  :deep(.design-upload-cta) {
    position: absolute;
    top: 50%;
    right: 24px;
    left: 24px;
    max-width: 320px;
    margin: auto;
    transform: translateY(-50%);
    background-color: $white;
    font-family: $font-family-secondary;
  }
}
</style>
